<template>
  <StepLayout
    :on-save="onSave"
    :is-busy-on-save="isBusyOnSave"
    :on-next="onNext"
    :is-busy-on-next="isBusyOnNext"
    :on-back="onBack"
    :is-disabled="isDisabled"
    step-navigation-button-data-ec="bulk_send_reminders_page"
  >
    <template v-slot:title>
      {{ $t('bulkSend.steps.reminders.title') }}
    </template>
    <p class="text-black">{{ $t('bulkSend.steps.reminders.description') }}</p>
    <div class="flex flex-col sm:grid-flow-col sm:auto-cols-max sm:grid sm:grid-rows-auto4 sm:gap-x-xxl gap-xs mb-base">
      <div
        v-for="option in reminderOptions"
        class="flex items-center first:col-span-2 mb-sm first:mb-twenty"
        :key=option.value
      >
        <VCheckbox
          :id="`reminder.${option.value}`"
          name="reminder"
          :checked="reminder === option.value"
          @input="updateReminder(option.value)"
          type="radio"
          data-ec="bulk_send_reminders_page"
          :data-et="`radio_option_${option.value}`"
        />
        <label
          :for="`reminder.${option.value}`"
          class="cursor-pointer pl-base"
        >
          {{ option.option }}
        </label>
      </div>
    </div>

    <HeightTransition>
      <FloatingLabelInput
        v-if="isCustomReminder"
        class="mt-base"
        type="number"
        :min="0"
        :step="1"
        :value="customReminder"
        @input="updateCustomReminder"
        :fieldIsRequired="false"
        :placeholder="$t('bulkSend.steps.reminders.customInterval')"
        :aria-label="$t('bulkSend.steps.reminders.customInterval')"
        data-test="reminders-custom_interval"
        data-hj-suppress
        data-ec="bulk_send_reminders_page"
        data-et="custom_interval_input"
      />
    </HeightTransition>
  </StepLayout>
</template>

<script>
import { computed, ref } from 'vue';
import { useGetters } from 'vuex-composition-helpers/dist';

import { useBulkSendStepsNavigation } from '@/composables/bulkSend/steps';

import VCheckbox from '@/components/deprecated/atoms/Checkbox.vue';
import StepLayout from '@/components/ui/bulkSend/StepLayout.vue';
import FloatingLabelInput from '@/components/tailwind/forms/FloatingLabelInput.vue';
import i18n from '@/locales';
import HeightTransition from '@/transitions/HeightTransition.vue';

export default {
  name: 'RemindersPage',
  components: {
    StepLayout,
    VCheckbox,
    HeightTransition,
    FloatingLabelInput,
  },
  setup() {
    // get bulk send data
    const { bulkSend } = useGetters({ bulkSend: 'bulkSend/bulkSend' });

    // initialise local state
    const reminderOptions = [
      { value: 0, option: i18n.t('bulkSend.steps.reminders.options.noReminders') },
      { value: 1, option: i18n.t('bulkSend.steps.reminders.options.everyday') },
      { value: 2, option: i18n.t('bulkSend.steps.reminders.options.every2Days') },
      { value: 3, option: i18n.t('bulkSend.steps.reminders.options.every3Days') },
      { value: 5, option: i18n.t('bulkSend.steps.reminders.options.every5Days') },
      { value: 7, option: i18n.t('bulkSend.steps.reminders.options.every7Days') },
      { value: 'custom', option: i18n.t('bulkSend.steps.reminders.options.custom') },
  ];
    const reminderValues = reminderOptions.map(option => option.value);

    const reminderDays = computed(() => Math.floor(bulkSend.value.envelope_reminder_interval / 24));
    const customReminder = ref(!reminderValues.includes(reminderDays.value) ? reminderDays.value : null);

    const reminder = ref(customReminder.value ? 'custom' : reminderDays.value);

    const updateValue = computed(() => ({
      envelope_reminder_interval:
        reminder.value !== 'custom'
          ? reminder.value * 24
          : customReminder.value * 24,
    }));
    const isValid = computed(() => reminder.value !== 'custom' || customReminder.value > 0);

    // Tell bulk send steps the action and
    // the data to dispatch update when navigating to next page
    const {
      onNext,
      isBusyOnNext,
      onSave,
      isBusyOnSave,
      onBack,
      isDisabled,
    } = useBulkSendStepsNavigation(
      'bulkSend/updateBulkSend',
      updateValue,
      isValid,
    );

    return {
      reminderOptions,
      customReminder,
      reminder,
      bulkSend,
      onNext,
      isBusyOnNext,
      onSave,
      isBusyOnSave,
      onBack,
      isDisabled,
    };
  },
  computed: {
    isCustomReminder() {
      return this.reminder === 'custom';
    },
  },
  methods: {
    updateCustomReminder(value) {
      this.customReminder = parseInt(value, 10);
    },
    updateReminder(value) {
      this.customReminder = null;
      this.reminder = value;
    },
  },
};
</script>

